import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Image from '@xingternal/malt-image';
import { variables } from '@xingternal/malt-predicates';
import {
  fallbackFemale,
  fallbackMale,
  fallbackNeutral,
} from './UserImageFallbacks';
import styles from './UserImage.css';
import testable from '@xingternal/malt-util/testable';
import { QA_MALT } from '@xingternal/malt-constants';
import ProfileImage from '@xingternal/profile-image';

import { withFlavour } from '@xingternal/flavour';

function multiplySize(pxString, factor) {
  const match = pxString.match(/(\d+)px/);
  if (!match) {
    return pxString;
  }
  return `${Number(match[1]) * factor}px`;
}

const sizes = {
  '100': variables.space8,
  '200': variables.space12,
  '300': variables.space16,
  '400': variables.space24,
  '500': multiplySize(variables.space16, 2),
  '600': multiplySize(variables.space16, 3),
  '700': multiplySize(variables.space16, 4),
};

const profileImageSizes = {
  '100': 'xsmall',
  '200': 'small',
  '300': 'medium',
  '400': 'large',
  '500': 'xlarge',
  '600': 'xxlarge',
  '700': 'xxxlarge',
};

const profileImageSizesConfined = {
  '100': 'xsmallOnConfined',
  '200': 'smallOnConfined',
  '300': 'mediumOnConfined',
  '400': 'largeOnConfined',
  '500': 'xlargeOnConfined',
  '600': 'xxlargeOnConfined',
  '700': 'xxxlargeOnConfined',
};
const profileImageSizesWide = {
  '100': 'xsmallOnWide',
  '200': 'smallOnWide',
  '300': 'mediumOnWide',
  '400': 'largeOnWide',
  '500': 'xlargeOnWide',
  '600': 'xxlargeOnWide',
  '700': 'xxxlargeOnWide',
};

const typexdl = {
  f: 'female-user',
  m: 'male-user',
  n: 'neutral-user',
};

const TestableFigure = testable(QA_MALT, 'figure');

const UserImageOdl = ({
  size = '200',
  sizeConfined,
  sizeWide,
  gender,
  srcSet,
  src,
  userName,
  connectionDegree,
  className,
  testId,
  ...props
}) => {
  const classes = classnames(
    styles.wrapper,
    {
      [styles['base-' + size]]: size || size === 0,
      [styles['baseConf-' + sizeConfined]]: sizeConfined || sizeConfined === 0,
      [styles['baseWide-' + sizeWide]]: sizeWide || sizeWide === 0,
    },
    className
  );

  const imageClasses = classnames(styles.image, {
    [styles['base-' + size]]: size || size === 0,
    [styles['baseConf-' + sizeConfined]]: sizeConfined || sizeConfined === 0,
    [styles['baseWide-' + sizeWide]]: sizeWide || sizeWide === 0,
  });

  const genderImg = gender
    ? gender === 'f'
      ? fallbackFemale
      : fallbackMale
    : fallbackNeutral;

  const imageSrc = src || genderImg;

  return (
    <TestableFigure className={classes} testId={testId}>
      <Image
        alt={userName}
        src={imageSrc}
        srcSet={srcSet}
        sizes={sizes[size] || sizes[sizeConfined] || sizes[sizeWide]}
        className={imageClasses}
        {...props}
      />
      {connectionDegree && (
        <figcaption
          className={classnames(styles.connectionDegree, styles.connection)}
        >
          {connectionDegree}
        </figcaption>
      )}
    </TestableFigure>
  );
};

const UserImage = ({
  flavour,
  className,
  size,
  sizeConfined,
  sizeWide,
  src,
  userName,
  gender,
  connectionDegree,
  ...props
}) => {
  if (flavour === 'xdl') {
    const xdlProps = {
      ...props,
      [profileImageSizes[size]]: Boolean(profileImageSizes[size]),
      [profileImageSizesConfined[sizeConfined]]: Boolean(
        profileImageSizesConfined[sizeConfined]
      ),
      [profileImageSizesWide[sizeWide]]: Boolean(
        profileImageSizesWide[sizeWide]
      ),

      type: typexdl[gender],
      skin: { container: className },
      src,
      connectionDegree,
      profileName: userName,
      ...props,
    };
    return <ProfileImage {...xdlProps} />;
  } else {
    return (
      <UserImageOdl
        size={size}
        sizeConfined={sizeConfined}
        sizeWide={sizeWide}
        gender={gender}
        className={className}
        src={src}
        connectionDegree={connectionDegree}
        userName={userName}
        {...props}
      />
    );
  }
};

UserImageOdl.propTypes = {
  /** Provides the user's name as alternative text for the image. It is required. */
  userName: PropTypes.string.isRequired,
  /** Define the basic `src` for the user image. This falls back to a neutral or a gendered SVG if you do not provide a `src`. The gendered SVG only renders if you pass a `gender` prop. */
  src: PropTypes.string,
  /** Provide a set of possible image sources and let the browser decide what it should render. Best and easiest way to ensure proper images on e.g. retina displays. IE11 does not support srcet and will always use src. */
  srcSet: PropTypes.string,
  /** Define the image size that should be shown in steps of 100 between `100` and `700`. This defaults to `200` if you don't pass along a size. */
  size: PropTypes.oneOf(['100', '200', '300', '400', '500', '600', '700']),
  /** Define the image size in tablet view is is nessesay to change UI in diffrent breaking points.  */
  sizeConfined: PropTypes.oneOf([
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
  ]),
  /** Define the image size in desktop view is is nessesay to change UI in diffrent breaking point.  */
  sizeWide: PropTypes.oneOf(['100', '200', '300', '400', '500', '600', '700']),
  /** Show the connection degree between the shown user and the current user. Has to be a number. You can include any number, but normally we only show the first or second connection degree. */
  connectionDegree: PropTypes.number,
  /** Define the shown user's gender – can be either `'m'` or `'f'`. If left out, the fallback image defaults to the neutral one. */
  gender: PropTypes.oneOf(['m', 'f']),
  /** add a custom className to the component */
  className: PropTypes.string,
  /** define a testid for the image */
  testId: PropTypes.string,
};

UserImage.displayName = 'UserImage';

UserImage.propTypes = { ...UserImageOdl.propTypes, flavour: PropTypes.string };

export default withFlavour(UserImage);
